<script setup lang="ts">
import { onMounted, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useCartStore } from '@/stores/cart';

// Cart popover is a *list-only* preview now. Terms, gap checks, and the
// actual order-creation POST all live on /checkout — the cart is just
// "here's what you have, click to review and pay."
const cart = useCartStore();
const router = useRouter();

const totalLabel = computed(() => `${(cart.totalCents / 100).toFixed(2)} €`);

function priceLabel(cents: number) {
  return `${(cents / 100).toFixed(2)} €`;
}

function categoryFor(name: string): string {
  const n = name.toLowerCase();
  if (/knife|karambit|bayonet|huntsman|navaja|stiletto|talon|ursus|paracord|nomad|skeleton|butterfly/.test(n)) return 'Knife';
  if (/glove|wraps/.test(n)) return 'Gloves';
  if (/awp|ssg/.test(n)) return 'Sniper';
  if (/ak-47|m4a4|m4a1|aug|sg|famas|galil/.test(n)) return 'Rifle';
  if (/glock|usp|p2000|p250|five|cz|tec|deagle|desert|r8|berettas/.test(n)) return 'Pistol';
  if (/mp|mac|ump|p90|bizon/.test(n)) return 'SMG';
  return 'Skin';
}

// Presentational only — maps the inferred category onto an Epic Cherry
// rarity token so the thumbnail card is rarity-tinted (bg glow + border).
function thumbStyle(name: string) {
  const cat = categoryFor(name);
  const rarity =
    cat === 'Knife' ? 'knife'
    : cat === 'Gloves' ? 'gloves'
    : cat === 'Sniper' ? 'classified'
    : cat === 'Rifle' ? 'covert'
    : cat === 'Pistol' ? 'restricted'
    : cat === 'SMG' ? 'mil-spec'
    : 'restricted';
  const v = `var(--color-rarity-${rarity})`;
  return {
    borderColor: v,
    background: `radial-gradient(circle at center, color-mix(in srgb, ${v} 24%, transparent) 0%, var(--color-surface-deep) 78%)`,
  };
}

function goToCheckout() {
  cart.close();
  router.push('/checkout');
}

// Esc closes; no backdrop click trap so the rest of the page stays interactive.
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && cart.open) cart.close();
}
onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
  <Teleport to="body">
    <!--
      Right-side panel on lg+ — narrow card pinned to the top-right with no
      backdrop so the rest of the page stays interactive.
      Mobile (< sm) — bottom sheet that fills the viewport width with a
      dim backdrop, since the right-side card would either clip or look
      like it's floating in nowhere on a 375 px screen.
    -->
    <div v-if="cart.open">
      <!-- Mobile backdrop (sm:hidden). Tap to close. -->
      <button
        type="button"
        class="fixed inset-0 z-40 bg-black/60 sm:hidden border-0 cursor-default"
        aria-label="Close cart"
        @click="cart.close()"
      />

      <!-- Panel — full-width sheet on mobile, right-side card on sm+. -->
      <div
        role="dialog"
        aria-label="Cart"
        class="fixed z-50 flex flex-col overflow-hidden bg-[var(--color-surface)] border border-[var(--color-border-medium)]
               shadow-[0_12px_32px_rgba(0,0,0,0.6)]
               inset-x-3 bottom-3 top-auto max-h-[80vh] rounded-[16px]
               sm:inset-auto sm:right-4 sm:bottom-auto sm:top-20 sm:w-[360px] sm:max-h-[calc(100vh-96px)] lg:right-6"
      >
        <header class="px-5 pt-5 pb-3 flex items-center justify-between">
          <div class="flex items-center gap-[10px]">
            <h2 class="font-display text-[20px] font-bold text-[var(--color-text)] leading-none">Cart</h2>
            <span
              v-if="!cart.isEmpty"
              class="px-2 py-[3px] rounded-full bg-[var(--color-accent-dim)] border border-[var(--color-accent-ring)]
                     font-display font-bold text-[11px] leading-none tracking-[0.4px] text-[var(--color-accent)] whitespace-nowrap"
            >
              {{ cart.count }} {{ cart.count === 1 ? 'item' : 'items' }}
            </span>
          </div>
          <button
            class="w-7 h-7 rounded-full bg-[var(--color-surface-2)] hover:bg-[var(--color-surface-3)] text-[var(--color-text-dim)]
                   hover:text-[var(--color-text)] text-[18px] leading-none border-0 cursor-pointer flex items-center justify-center transition-colors"
            aria-label="Close cart"
            @click="cart.close()"
          >
            ×
          </button>
        </header>

        <div class="h-px bg-[var(--color-border)]" aria-hidden="true" />

        <!-- Empty state -->
        <div
          v-if="cart.isEmpty"
          class="flex-1 px-5 py-10 flex flex-col items-center justify-center gap-4 text-center"
        >
          <p class="font-body text-[14px] leading-[21px] text-[var(--color-text-dim)]">
            Your cart is empty. Browse the marketplace and add a skin to start a checkout.
          </p>
          <button
            class="px-5 py-3 rounded-full font-display font-bold text-[13px] tracking-[0.5px] text-[#581e00]
                   hover:opacity-90 transition-opacity border-0 cursor-pointer"
            :style="{ backgroundImage: 'linear-gradient(90deg, var(--color-accent-from) 0%, var(--color-accent-to) 100%)' }"
            @click="cart.close(); router.push('/catalog')"
          >
            Browse marketplace
          </button>
        </div>

        <!-- Filled state -->
        <template v-else>
          <ul class="flex-1 overflow-y-auto px-5 py-3 space-y-3">
            <li
              v-for="item in cart.items"
              :key="item.listingId"
              class="flex items-center gap-3"
            >
              <!-- Item image — rarity-tinted thumb -->
              <div
                class="relative size-[66px] rounded-[10px] overflow-hidden flex-shrink-0 flex items-center justify-center border"
                :style="thumbStyle(item.marketHashName)"
              >
                <img
                  :src="item.imageUrl || '/figma/skins/card-placeholder.png'"
                  :alt="item.marketHashName"
                  class="relative max-w-full max-h-full object-contain"
                />
              </div>

              <div class="flex-1 min-w-0">
                <p class="font-display uppercase text-[9px] tracking-[0.5px] text-[var(--color-accent)] leading-none">
                  {{ categoryFor(item.marketHashName) }}
                </p>
                <p class="mt-1 font-display text-[12px] font-semibold text-[var(--color-text)] leading-tight line-clamp-2">
                  {{ item.marketHashName }}
                </p>
                <p class="mt-1 font-display font-bold text-[13px] text-[var(--color-text-soft)]">
                  {{ priceLabel(item.ourPriceCents) }}
                </p>
              </div>

              <button
                class="flex-shrink-0 size-[32px] rounded-full bg-transparent border-0 text-[var(--color-accent)]
                       hover:bg-[var(--color-surface-2)] cursor-pointer flex items-center justify-center transition-colors"
                :aria-label="`Remove ${item.marketHashName}`"
                @click="cart.remove(item.listingId)"
              >
                <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
                  <path d="M2 4H12M5.5 4V2.5C5.5 2.22 5.72 2 6 2H8C8.28 2 8.5 2.22 8.5 2.5V4M3 4V13.5C3 13.78 3.22 14 3.5 14H10.5C10.78 14 11 13.78 11 13.5V4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
                </svg>
              </button>
            </li>
          </ul>

          <div class="h-px bg-[var(--color-border)]" aria-hidden="true" />

          <!-- Footer: total + go-to-checkout -->
          <footer class="px-5 py-4 flex flex-col gap-3">
            <div class="flex items-center justify-between">
              <span class="font-display text-[14px] text-[var(--color-text-dim)]">Subtotal</span>
              <span class="font-display font-bold text-[18px] tracking-[-0.3px] text-[var(--color-accent)]">{{ totalLabel }}</span>
            </div>
            <div class="flex items-center gap-2">
              <button
                class="flex-1 h-[40px] rounded-full font-display font-bold text-[13px] tracking-[0.5px] text-[#581e00]
                       hover:opacity-90 transition-opacity border-0 cursor-pointer"
                :style="{ backgroundImage: 'linear-gradient(90deg, var(--color-accent-from) 0%, var(--color-accent-to) 100%)' }"
                @click="goToCheckout"
              >
                Go to checkout →
              </button>
              <button
                class="h-[40px] px-4 rounded-full bg-transparent border border-[var(--color-border-strong)] font-display text-[12px]
                       tracking-[0.5px] text-[var(--color-text-dim)] hover:border-[var(--color-accent)] hover:text-[var(--color-text)]
                       transition-colors cursor-pointer"
                @click="cart.clear()"
              >
                Clear
              </button>
            </div>
          </footer>
        </template>
      </div>
    </div>
  </Teleport>
</template>
